import * as React from "react"
import {
  FaArrowRight,
  FaArrowUp,
  FaBars,
  FaCheckCircle,
  FaEnvelope,
  FaFacebookF,
  FaGoogle,
  FaInstagram,
  FaMapMarkerAlt,
  FaPhoneAlt,
} from "react-icons/fa"
import TradeIcon from "../TradeIcon"
import QuoteForm from "./QuoteForm"
import { buildView } from "./view-model"
import "../../../styles/templates/base.css"
import "../../../styles/templates/v2.css"
import "../../../styles/templates/v2.mobile.css"
import "../../../styles/templates/port.css"

/**
 * Free Template 1 — the Mockup Maker's v2 design, ported — MTC-FCS-041.
 * ============================================================================
 * The same DOM and the same stylesheet as
 * `source--mockup-maker/src/templates/v2`, rendered from this builder's draft.
 * The CSS is copied VERBATIM (src/styles/templates/v2.css); only the data
 * plumbing is new, which is what keeps the two looking identical instead of
 * merely similar.
 *
 * ── MISSING FACTS REMOVE ELEMENTS ──────────────────────────────────────────
 * The original gates every claim on the claim being true, and so does this.
 * This builder collects less than the maker does — no opening hours, street
 * address, review score or years trading — so those blocks simply do not
 * render, exactly as they would not in the maker with those fields blank.
 * Nothing is filled in with a plausible-looking stand-in.
 *
 * ── THE PICTURES STAY CLICKABLE ────────────────────────────────────────────
 * `data-slot` is kept on every image, which is what the builder's picture
 * picker (MTC-FCS-027) listens for inside the preview frame.
 */

/** Their `<Photo>`, reduced to what this builder needs. */
/**
 * ── LOCAL OR REMOTE — FCS-057(b) ───────────────────────────────────────────
 * DEFAULT_MODE is the switch this design runs in when nobody says otherwise,
 * and it is "local": the content is whatever the builder hands over, which is
 * the draft somebody is editing.
 *
 * In "remote" mode the same component draws a PUBLISHED site, and the JSON
 * fetched from Storage is the only source of anything — text, photographs,
 * contact details, colours. Nothing here reaches for a sample value in that
 * mode, because a placeholder on a real contractor's domain is a sentence
 * they never wrote on a website customers are reading. The refusing is done
 * before this point, in lib/site-content.js: a file that is missing a
 * required field never reaches a template at all.
 *
 * TEMPLATE_VERSION is what a published file is checked against, so a site
 * published against an older shape of this design is refused rather than
 * rendered wrong.
 */
export const DEFAULT_MODE = "local"
export const TEMPLATE_ID = "mm-v2"
export const TEMPLATE_VERSION = "1"

const Photo = ({ src, slot, alt }) =>
  src ? (
    <div className="mk-photo" data-slot={slot}>
      <img src={src} alt={alt || ""} />
    </div>
  ) : (
    <div className="mk-photo" data-slot={slot} aria-hidden="true" />
  )

const TemplateV2 = ({ draft, plan, mode = DEFAULT_MODE }) => {
  const v = buildView(draft, { plan, mode })

  const navItems = [
    { href: "#top", label: "Home" },
    { href: "#services", label: "Services" },
    ...(v.hasGallery ? [{ href: "#gallery", label: "Our Work" }] : []),
    { href: "#about", label: "About" },
    { href: "#contact", label: "Contact" },
  ]

  // The credibility band is all claims about the business. With only the
  // service area to show, one tile is honest; with nothing, the band goes.
  const showLocation = Boolean(v.serviceArea)

  // "Why choose us" from the work actually offered — true by construction,
  // rather than invented credentials.
  const why = v.services.slice(0, 3)

  return (
    <div className="mk mk-v2" style={v.style} data-mode="light" id="top">
      {/* ── HEADER ─────────────────────────────── */}
      <header>
        <nav className="v2-nav">
          <div className="v2-container v2-nav-inner">
            <div className="v2-logo">
              <span className="mk-monogram v2-logo-mark">{v.initials}</span>
              <span className="v2-logo-text">
                <span className="v2-logo-name">
                  {v.lead} {v.accent && <em>{v.accent}</em>}
                </span>
                {v.slogan && <span className="v2-logo-tag">{v.slogan}</span>}
              </span>
            </div>

            <div className="v2-nav-right">
              <ul className="v2-nav-links">
                {navItems.map((link, i) => (
                  <li key={link.label}>
                    <a href={link.href} className={`v2-nav-link${i === 0 ? " is-active" : ""}`}>
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
              <a href="#contact" className="v2-nav-cta">
                Get Free Quote
              </a>
            </div>

            <span className="v2-burger">
              <FaBars />
            </span>
          </div>
        </nav>
      </header>

      {/* ── HERO ───────────────────────────────── */}
      <section className="v2-hero">
        <Photo src={v.heroImage} slot="hero" />
        <div className="v2-hero-overlay" />
        <div className="v2-hero-vignette" />

        <div className="v2-hero-inner">
          <div className="v2-hero-grid">
            <div>
              <span className="v2-badge">
                <FaCheckCircle />
                <span>{v.name}</span>
              </span>

              <h1 className="v2-headline">
                {v.trade}
                {v.cityIsReal && <em>Across {v.city}</em>}
              </h1>

              {v.aboutShort && <p className="v2-hero-sub">{v.aboutShort}</p>}

              {/* "Free Estimates" and "Local & Family-Owned" were drawn here for every contractor. They are claims, and nobody
                  made them: this builder never asks whether a business offers free estimates or is family owned, so a badge
                  saying so is a promise on their behalf that they may not be able to keep. (FCS-124) */}

              <div className="v2-cta-row">
                {v.hasPhone && (
                  <a href={v.phoneHref} className="v2-btn v2-btn-primary">
                    <FaPhoneAlt /> {v.phone}
                  </a>
                )}
                <a
                  href="#services"
                  className={`v2-btn ${v.hasPhone ? "v2-btn-secondary" : "v2-btn-primary"}`}
                >
                  View Services
                </a>
              </div>
            </div>

            <div className="v2-form-card" id="contact">
              <div className="v2-form-head">
                <h3>Request a Free Estimate</h3>
                <p>Fill out the form below and we&apos;ll get back to you</p>
              </div>

              {/* Real fields on Lead Generation, and on every other tier a
                  card that does the one real thing available rather than a
                  picture of a form that goes nowhere — MTC-FCS-017. */}
              {v.wantsForm ? (
                <QuoteForm view={v} variant="v2" />
              ) : (
                <div className="v2-form-plain">
                  {v.hasPhone && (
                    <a href={v.phoneHref} className="v2-form-line">
                      <FaPhoneAlt /> {v.phone}
                    </a>
                  )}
                  {v.hasEmail && (
                    <a href={v.emailHref} className="v2-form-line">
                      <FaEnvelope /> {v.email}
                    </a>
                  )}
                  {v.serviceArea && (
                    <span className="v2-form-line is-plain">
                      <FaMapMarkerAlt /> {v.serviceArea}
                    </span>
                  )}
                  {v.hasPhone || v.hasEmail ? (
                    <a href={v.hasPhone ? v.phoneHref : v.emailHref} className="v2-submit">
                      {v.hasPhone ? "Call for a free estimate" : "Email for a free estimate"}
                    </a>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── CREDIBILITY BAND ───────────────────── */}
      {showLocation && (
        <section className="v2-stats">
          <div className="v2-stats-inner">
            <div className="v2-stats-grid" data-count={1}>
              <div className="v2-stat">
                <span className="v2-stat-icon">
                  <FaMapMarkerAlt />
                </span>
                <span className="v2-stat-text">
                  <strong>{v.serviceArea}</strong>
                  <span>Where we work</span>
                </span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── SERVICES ───────────────────────────── */}
      <section className="v2-services" id="services">
        <div className="v2-container">
          <div className="v2-sec-head">
            <span className="v2-kicker">Our Services</span>
            <h2 className="v2-sec-title">What We Do</h2>
            {v.serviceArea ? <p className="v2-sec-sub">Serving {v.serviceArea}.</p> : null}
          </div>

          <div className="v2-grid">
            {v.services.map((service, i) => (
              <article className="v2-card" key={service.slug}>
                <div className="v2-card-media">
                  <Photo src={service.image} slot={`service${i + 1}`} />
                  {!service.image && (
                    <span className="v2-card-glyph">
                      <TradeIcon name={service.icon} />
                    </span>
                  )}
                  <div className="v2-card-media-veil" />
                  <span className="v2-card-icon">
                    <TradeIcon name={service.icon} />
                  </span>
                </div>
                <div className="v2-card-body">
                  <h3 className="v2-card-title">{service.title}</h3>
                  <p className="v2-card-desc">{service.description}</p>
                  <span className="v2-card-link">
                    Explore Service <FaArrowRight />
                  </span>
                </div>
              </article>
            ))}
          </div>

          <div className="v2-svc-cta">
            <div>
              <h3>Need something else?</h3>
              <p>
                If you don&apos;t see what you need listed here, reach out and we&apos;ll help you
                find the right solution.
              </p>
            </div>
            {v.hasPhone && (
              <a href={v.phoneHref} className="v2-svc-cta-btn">
                <FaPhoneAlt /> Call Now
              </a>
            )}
          </div>
        </div>
      </section>

      {/* ── OUR WORK ───────────────────────────── */}
      {v.hasGallery && (
        <section className="v2-work" id="gallery">
          <div className="v2-container">
            <div className="v2-sec-head">
              <span className="v2-kicker">Our Work</span>
              <h2 className="v2-sec-title">Recent jobs, straight off the van.</h2>
              {v.areaLine && <p className="v2-sec-sub">{v.areaLine}.</p>}
            </div>
            <div className="v2-work-grid">
              {v.gallery.slice(0, 6).map((url, i) => (
                <figure className="v2-work-tile mk-tile" key={`${url}-${i}`}>
                  <Photo src={url} slot={`gallery${i + 1}`} />
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── WHY CHOOSE US ──────────────────────── */}
      <section className="v2-why" id="about">
        <div className="v2-container">
          <div className="v2-why-grid">
            <div>
              <span className="v2-kicker">About Us</span>
              <h2 className="v2-sec-title">
                {v.trade} {v.cityIsReal && <em>Across {v.city}</em>}
              </h2>
              {v.aboutText && <p className="v2-sec-sub">{v.aboutText}</p>}

              {why.length > 0 && (
                <div className="v2-why-list">
                  {why.map(point => (
                    <div className="v2-why-item" key={point.slug}>
                      <span className="v2-why-icon">
                        <TradeIcon name={point.icon} />
                      </span>
                      <div>
                        <h3>{point.title}</h3>
                        <p>{point.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="v2-why-cta-row">
                <a href="#contact" className="v2-why-primary">
                  Get Free Estimate <FaArrowRight />
                </a>
              </div>
            </div>

            <div>
              <div className="v2-image-card">
                <Photo src={v.heroImage} slot="hero" />
                <div className="v2-image-veil" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ─────────────────────────────── */}
      <footer className="v2-footer">
        <div className="v2-container">
          <div className="v2-footer-grid" data-cols={v.hasPhone || v.hasEmail ? 4 : 3}>
            <div>
              <div className="v2-footer-logo-row">
                <span className="mk-monogram">{v.initials}</span>
                <div>
                  <p className="v2-footer-name">{v.name}</p>
                  {v.slogan && <p className="v2-footer-tagline">{v.slogan}</p>}
                </div>
              </div>
              {v.aboutShort && <p className="v2-footer-desc">{v.aboutShort}</p>}
            </div>

            {(v.hasPhone || v.hasEmail) && (
              <div>
                <p className="v2-col-title">Contact</p>
                {v.hasPhone && (
                  <a href={v.phoneHref} className="v2-link-row">
                    <span className="v2-ico">
                      <FaPhoneAlt />
                    </span>
                    {v.phone}
                  </a>
                )}
                {v.hasEmail && (
                  <a href={v.emailHref} className="v2-link-row">
                    <span className="v2-ico">
                      <FaEnvelope />
                    </span>
                    {v.email}
                  </a>
                )}
                {v.serviceArea && (
                  <span className="v2-link-row">
                    <span className="v2-ico">
                      <FaMapMarkerAlt />
                    </span>
                    {v.serviceArea}
                  </span>
                )}
              </div>
            )}

            <div>
              <p className="v2-col-title">Services</p>
              {v.services.map(service => (
                <a href="#services" className="v2-link" key={service.slug}>
                  {service.title}
                </a>
              ))}
            </div>

            <div>
              <p className="v2-col-title">Social Links</p>
              <div className="v2-social">
                <span className="v2-social-btn">
                  <FaFacebookF />
                </span>
                <span className="v2-social-btn">
                  <FaInstagram />
                </span>
                <span className="v2-social-btn">
                  <FaGoogle />
                </span>
              </div>
            </div>
          </div>

          <div className="v2-footer-bottom">
            <p className="v2-footer-copy">
              © {v.year} {v.name}. All Rights Reserved.
            </p>
            <a href="#top" className="v2-scroll-top">
              <FaArrowUp /> Back to Top
            </a>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default TemplateV2
